/**
 * postMessage protocol for the external ER-Diagram iframe. Mirrors
 * `wf-audit/src/slices/planning/domain/erd-bridge.constants.ts` verbatim
 * — we share the same iframe app (separate WorkFocus repo, hosted on
 * CloudFront) so the message vocabulary must stay aligned.
 *
 * The iframe sends `source: 'wcoe-erd'` messages; the parent (this SPA)
 * sends `source: 'wcoe-app'` messages. The origin check on incoming
 * messages enforces that responses only honour traffic from the iframe's
 * canonical URL (no XSS posing as the ER app).
 */

// Production CloudFront URL — same for legacy + new SPA. Override via
// VITE_ERD_IFRAME_URL when working against a preview deployment of the
// iframe app.
export const ERD_IFRAME_URL =
  (import.meta as { env?: { VITE_ERD_IFRAME_URL?: string } }).env?.VITE_ERD_IFRAME_URL ??
  'https://d7spx11d6b000.cloudfront.net';

export const SOURCE_INBOUND = 'wcoe-erd';
export const SOURCE_OUTBOUND = 'wcoe-app';

// Inbound message types (iframe → parent)
export const MSG_READY = 'READY';
export const MSG_REQUEST_WORKSPACES = 'REQUEST_WORKSPACES';
export const MSG_REQUEST_WORKSPACE_METADATA = 'REQUEST_WORKSPACE_METADATA';
export const MSG_OPEN_SCENARIO = 'OPEN_SCENARIO';
export const MSG_COPY_TO_CLIPBOARD = 'COPY_TO_CLIPBOARD';

// Outbound message types (parent → iframe)
export const MSG_SET_THEME = 'SET_THEME';
export const MSG_WORKSPACES_DATA = 'WORKSPACES_DATA';
export const MSG_WORKSPACE_METADATA = 'WORKSPACE_METADATA';
export const MSG_FUSION_USAGE_DATA = 'FUSION_USAGE_DATA';

// ─── Domain types — match the iframe app's expected shape ──────────────
// Lifted from `wf-audit/src/slices/planning/domain/planning.types.ts`.

export interface PlanningWorkspace {
  id: string;
  name: string;
  icon: string;
  createdBy: string;
}

export interface PlanningWorkspaceEnriched extends PlanningWorkspace {
  createdByName: string | null;
}

export interface PlanningTableField {
  id: string;
  name: string;
  type: string;
  hasExternalSource: boolean;
  externalOptions: Record<string, unknown> | null;
  sourceTableName: string | null;
  sourceFieldName: string | null;
  connectionType: string | null;
}

export interface PlanningRecordType {
  id: string;
  name: string;
  section: string;
  icon: string;
  color: string;
  fields: PlanningTableField[];
}

export interface PlanningRecordTypeSection {
  name: string;
  position: number;
}

export interface PlanningWorkspaceMetadata {
  recordTypes: PlanningRecordType[];
  recordTypeSections: PlanningRecordTypeSection[];
}

export interface ScenarioPlanningObjectItem {
  planningObjectType: 'workspace' | 'record_type' | 'field' | 'record';
  planningObjectId: string;
  workspaceId: string | null;
  workspaceName: string | null;
  recordTypeId: string | null;
  recordTypeName: string | null;
  objectName: string | null;
  fusionModuleId: string | null;
  fusionModuleName: string | null;
  fusionModuleType: string | null;
  fusionScenarioId: number | null;
  fusionScenarioName: string | null;
  scenarioId: number | null;
  teamFusionId: number | null;
}

// ─── Maestro raw response shapes (for the SPA-side mapper) ─────────────

export interface WfRawSection {
  id: string;
  displayName: string;
  recordTypeIds?: string[];
}

export interface WfRawReferenceOptions {
  isExternal?: boolean;
  multiple?: boolean;
  recordTypeId?: string;
  externalOptions?: Record<string, unknown>;
  backField?: { referenceOptions?: { multiple?: boolean } };
}

export interface WfRawLookupOptions {
  sourceField?: { id?: string; displayName?: string };
}

export interface WfRawField {
  id: string;
  displayName: string;
  type: string;
  referenceOptions?: WfRawReferenceOptions;
  lookupOptions?: WfRawLookupOptions;
  linkedField?: { displayName?: string };
}

export interface WfRawRecordType {
  id: string;
  displayName: string;
  icon: string;
  color: string;
  isExternal?: boolean;
  externalOptions?: Record<string, unknown>;
  fields?: WfRawField[];
}

export interface WfRawWorkspaceMetadataResponse {
  recordTypes?: WfRawRecordType[];
  recordTypeSections?: WfRawSection[];
}

export interface WfRawWorkspacesResponse {
  workspaces?: PlanningWorkspace[];
}

export interface WfRawUser {
  id: string;
  name: string;
}
